import { type ReactNode, type FormHTMLAttributes } from 'react';
import {
  useForm,
  type UseFormReturn,
  type FieldValues,
  type DefaultValues,
  type SubmitHandler,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { type ZodSchema } from 'zod';
import { cn } from '@/lib/utils';

export interface FormProps<T extends FieldValues> extends Omit<
  FormHTMLAttributes<HTMLFormElement>,
  'onSubmit' | 'children'
> {
  schema: ZodSchema<T>;
  defaultValues?: DefaultValues<T>;
  onSubmit: SubmitHandler<T>;
  children: (form: UseFormReturn<T>) => ReactNode;
  className?: string;
  /** Test identifier for automation (Playwright). */
  'data-testid'?: string;
}

/**
 * Form wrapper that integrates react-hook-form with Zod validation.
 * Provides the form instance to children via render prop pattern.
 */
export function Form<T extends FieldValues>({
  schema,
  defaultValues,
  onSubmit,
  children,
  className,
  'data-testid': testId,
  ...formProps
}: FormProps<T>) {
  const form = useForm<T>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onBlur',
  });

  return (
    <form
      {...formProps}
      data-testid={testId}
      onSubmit={form.handleSubmit(onSubmit)}
      className={cn('space-y-4', className)}
      noValidate
    >
      {children(form)}
    </form>
  );
}

export interface FormSectionProps {
  title?: string;
  description?: string;
  children: ReactNode;
  className?: string;
}

export function FormSection({ title, description, children, className }: FormSectionProps) {
  return (
    <fieldset className={cn('space-y-4', className)}>
      {(title || description) && (
        <legend className="mb-4">
          {title && (
            <span className="block text-base font-semibold text-gray-900 dark:text-gray-100">
              {title}
            </span>
          )}
          {description && (
            <span className="mt-1 block text-sm text-gray-500 dark:text-gray-400">
              {description}
            </span>
          )}
        </legend>
      )}
      {children}
    </fieldset>
  );
}

export interface FormActionsProps {
  children: ReactNode;
  className?: string;
}

export function FormActions({ children, className }: FormActionsProps) {
  return (
    <div className={cn('flex items-center justify-end gap-3 pt-4', className)}>{children}</div>
  );
}
